import React from 'react'
import classNames from 'classnames'
import { TIconSize } from 'transfix-ui/components/SvgIcon/types'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentIssueStates } from 'types/graphqlTypes'
import { ShipmentIssue } from 'apps/marketplace/apiTypes/shipmentIssues'
import { DISPLAY } from 'constants/display'
import SLAAlertIcon from './SLAAlertIcon'
import { TIconColor } from './types'
import { useSLADueStatus } from './useSLADueStatus'

interface ISLADueStatusProps {
  priority: ShipmentIssue['priority']
  /**
   * @default true
   */
  showIcon?: boolean
  /**
   * @default true
   */
  showLabel?: boolean
  dueAt: ShipmentIssue['dueAt']
  issueStatus: IShipmentIssueStates
  /**
   * @default xlarge
   */
  iconSize?: TIconSize
  className?: string
}

const useSLADueStatusStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: theme.spacing(0.75),
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  label: {
    color: ({ color }: { color: TIconColor }) => (color ? brandColors[color] : ''),
    width: 'fit-content',
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    width: '20px',
  },
}))

/**
 * SLA Status Icons
 * If P1 or P2, error (red) alert icon is shown
 * If P3 or P4, warning (orange) alert icon is shown
 * If Past SLA, filled icon is shown
 * If Near SLA, line icon is shown
 */
const SLADueStatus = ({
  className,
  dueAt,
  iconSize = 'xlarge',
  issueStatus,
  priority,
  showIcon: showIconProp = true,
  showLabel: showLabelProp = true,
}: ISLADueStatusProps) => {
  const { color, isNearSLA, isPastSLA, showStatus, slaLabel } = useSLADueStatus({
    dueAt,
    issueStatus,
    priority,
  })

  const classes = useSLADueStatusStyles({ color })
  const containerClasses = classNames(classes.container, className)

  const showAlertIcon = showIconProp && showStatus
  const showLabel = showLabelProp && showStatus

  return (
    <div className={containerClasses}>
      <div className={classes.icon}>
        {showAlertIcon && (
          <SLAAlertIcon
            isNearSLA={isNearSLA}
            isPastSLA={isPastSLA}
            color={color}
            iconSize={iconSize}
          />
        )}
      </div>
      {showLabel ? (
        <Typography variant='body1' className={classes.label}>
          {slaLabel}
        </Typography>
      ) : (
        <Typography variant='body1'>{DISPLAY.empty}</Typography>
      )}
    </div>
  )
}

export default SLADueStatus
